<template>
    <div class="plan" :class="{'drag-active': dragActive}">
        <div :class="{hidden: this.commentsHidden }" class="plan__comment">
            <span @click="getPlanCommentRemoveClickListener()"
                  :class="{hidden: !plan.hasComment()}"
                    class="plan__comment_button" role="button" title="Usuń komentarz">
                <i class="fa fa-times text-red"></i>
            </span>
            <span @click="getPlanCommentAddClickListener()"
                  :class="{hidden: plan.hasComment()}"
                  class="plan__comment_button"
                  role="button"
                  ref="addPlanComment"
                  title="Dodaj komentarz"
                >
                <i class="fa fa-commenting text-success"></i>
            </span>
            <p class="plan__comment_input" contenteditable="true"
               ref="planComment"
               placeholder="Komentarz..."
               title="Kliknij by edytować"
               :style="{visibility: planCommentInputVisibility}"
               v-comment-input-click-outside="{
                    exclude: ['planComment', 'addPlanComment'],
                    handler: 'getPlanCommentOnOutsideClickListener',
               }"
               @blur="getPlanCommentOnBlurListener($event)"
               v-html="$sanitize(plan.comment)"
            >
            </p>
        </div>
        <div class="plan__card">
            <div class="plan__card_details">
                <div class="plan__header d-flex justify-content-between">
                    <h2 class="plan__name item--offset" @click="addItemsModal" title="Zarządzaj posiłkiem">
                        <i class="fa fa-plus-circle text-success"></i> {{plan.name}}
                    </h2>
                    <dropdown :position="isFirstDay? 'left':'right'" :disabled="dragAndCopyEnabled" class="dropdown-actions" v-show="hasPermission('creator_meals_manage')">

                        <li role="presentation" v-if="hasPermission('creator_plan_edit')">
                            <a @click="showModal({ component: 'edit-plan', data: {dayId: day.id, plan: plan.id}, options:
                    {large: false} })" role="button" class="hover-success" title="Edytuj dane posiłku">
                                <i class="fa fa-edit"></i> Edytuj
                            </a>
                        </li>
                        <li role="presentation" v-if="hasPermission('creator_plan_copy')">
                            <v-popover>
                                <a class="hover-success" title="Kopiuj posiłek"><i class="fa fa-copy"></i>
                                    Kopiuj</a>
                                <template slot="popover">
                                    <copy-plan :dayId="day.id" :planId="plan.id"></copy-plan>
                                </template>
                            </v-popover>
                        </li>
                        <li role="presentation" >
                            <a class="disabled" v-if="!hasPermission('creator_copy_to_another_menu')"
                                  v-tooltip="trans('app.no_access_tooltip')"><i class="fa fa-copy"></i>
                              {{ 'app.menu.copy_to_another_menu' | trans }}</a>
                            <a v-else @click="copyPlanToAnotherMenu(plan)"><i class="fa fa-copy"></i>
                              {{ 'app.menu.copy_to_another_menu' | trans }}</a>
                        </li>
                        <li role="presentation" v-if="hasPlanInClipboard">
                            <a @click="pastePlanFromAnotherMenu(plan)"><i class="fa fa-paste"></i>
                              {{ 'app.menu.paste_from_another_menu' | trans }}</a>
                        </li>
                        <li role="presentation">
                          <a @click="showModal({
                            component: 'plan-summary',
                            data: {
                              plan: plan
                            },
                            options: {large: true}
                          })" role="button" class="hover-success" :title="trans('app.nutrients.nutrients')">
                            <i class="fa fa-list-alt"></i> {{ trans('app.nutrients.nutrients') }}
                          </a>
                        </li>
                        <li role="presentation" v-if="day.getNumberOfPlans() > 1 && hasPermission('creator_plan_delete')" class="divider"></li>
                        <li role="presentation" v-if="day.getNumberOfPlans() > 1 && hasPermission('creator_plan_delete')">
                            <a @click="remove(plan.id)" role="button" class="hover-remove" title="Usuń posiłek">
                                <i class="fa fa-times text-red" aria-hidden="true"></i> Usuń
                            </a>
                        </li>
                    </dropdown>
                </div>
                <div class="plan__summary d-flex justify-content-between item--offset">
                    <span>
                        {{plan.time}}
                    </span>
                    <div>
                        <span :class="getCaloriesClasses()">{{plan.totalEnergy.amount | integers}}</span>
                        / {{plan.prescribedEnergy.amount}} kcal
                    </div>
                </div>

                <draggable
                    class="plan__items items__foods"
                    :list="plan.foods"
                    group='foods'
                    :draggable="`.item`"
                    :handle="`.item__label`"
                    @start="onFoodDragStart"
                    @end="onFoodDragEnd"
                    @choose="onItemChoose"
                    :data-time="plan.date"
                    :data-plan="plan.id"
                    :data-day="day.id"
                    :disabled="!hasPermission('creator_items_move')"
                >
                  <div v-for="food in plan.foods" :key="food.id" class="item" :data-id="food.id">
                    <div class="plan__item" :class="{
                            red: !food.isValid(),
                            'green-text': !food.isValid() || isFoodWithSearchedProduct(food),
                            'green-border': isFoodWithSearchedProduct(food),
                            yellow: isFoodWithReplacedProduct(food)
                          }"
                         @dblclick="editFoodModal(food.id)">
                      <div class="item__label">
                          <span id="repeat-product" v-show="hasPermission('creator_food_repetition')"
                                :class="{hidden: !food.repeating.length > 0}"
                                class="label label-default"
                                v-tooltip="repeatedFoods(food)"><i
                              class="fa fa-repeat"></i></span>

                        <span v-show="hasPermission('creator_products_preferences')"
                              class="label label-warning" :class="{hidden: !food.hasDisliked()}"
                              v-tooltip="'Produkt w tym daniu jest nielubiany przez pacjenta'">
                              <i class="fa fa-thumbs-down"></i></span>

                        <span v-show="hasPermission('creator_products_preferences')"
                              class="label label-danger"
                              :class="{hidden: !food.hasAllergens()}"
                              v-tooltip="'Produkt w tym daniu jest alergiczny dla pacjenta'">
                              <i class="fa fa-ban"></i></span>

                        <span v-bind:class="food.isMultiPortionFood() ? getMultiPortionFoodColorClass(food):''"
                              v-tooltip="food.isValid() ? food.name +
                          (food.portion.amount !== 1 ? ' (' + food.portion.amount + 'p.)' : '') : 'Sprawdź poprawność dania'">{{ food.name }}</span>

                        <span
                            :class="food.isMultiPortionFood() ? getMultiPortionFoodColorClass(food):''"
                            v-if="food.isMultiPortionFood() || food.portion.amount !== 1">({{ food.portion.amount }}p.)</span>

                        <span v-tooltip="leftPortionsForFoodToolTip(food)"
                              v-if="food.isMultiPortionFood()"
                              v-bind:class="{ 'red--text': getUsedPortionsOfFood(food) !== food.portions }"
                        >({{ getUsedPortionsOfFood(food) }}/{{ food.portions }}p.)</span>
                      </div>
                      <dropdown
                          :position="isFirstDay? 'left':'right'"
                          :disabled="dragAndCopyEnabled"
                          class="context__menu"
                          v-show="somePermission('creator_items_edit', 'creator_items_copy')">
                        <li role="presentation" v-if="hasPermission('creator_items_edit')">
                          <a @click="editFoodModal(food.id)" title="Edytuj danie">
                            <i class="fa fa-edit"></i> Edytuj
                          </a>
                        </li>
                        <li role="presentation" v-if="hasPermission('creator_items_copy')">
                          <v-popover>
                            <a class="hover-success" title="Kopiuj danie">
                              <i class="fa fa-copy"></i> Kopiuj
                            </a>
                            <template slot="popover">
                              <copy-food :dayId="day.id" :planId="plan.id"
                                         :foodId="food.id"></copy-food>
                            </template>
                          </v-popover>
                        </li>
                        <li role="presentation">
                          <a @click="showModal({
                                      component: 'food-summary',
                                      data: {
                                        food: food
                                      },
                                      options: {large: true}
                                    })" role="button" class="hover-success" :title="trans('app.nutrients.nutrients')">
                            <i class="fa fa-list-alt"></i> {{ trans('app.nutrients.nutrients') }}
                          </a>
                        </li>
                        <li role="presentation" class="divider"></li>
                        <li role="presentation" v-if="hasPermission('creator_items_delete')">
                          <a @click="removeFood(food.id)" title="Usuń danie">
                            <i class="fa fa-times text-red"></i> Usuń
                          </a>
                        </li>
                      </dropdown>
                    </div>
                  </div>
                </draggable>

                <draggable
                    class="plan__items items__products"
                    :list="plan.products"
                    group='products'
                    :draggable="`.item`"
                    :handle="`.item__label`"
                    @start="onProductDragStart"
                    @end="onProductDragEnd"
                    @choose="onItemChoose"
                    :data-time="plan.date"
                    :data-plan="plan.id"
                    :data-day="day.id"
                    :disabled="!hasPermission('creator_items_move')"
                >
                  <div v-for="product in plan.products" :key="product.id" class="item" :data-id="product.id">
                    <div class="plan__item" :class="{
                            'green-text': isSearchedProduct(product),
                            'green-border': isSearchedProduct(product),
                            yellow: isReplacedProduct(product)
                          }" @dblclick="addItemsModal(product)">
                      <div class="item__label">
                        <span v-show="hasPermission('creator_products_preferences')"
                              class="label label-warning"
                              :class="{hidden: !product.disliked}"
                              v-tooltip="'Produkt jest nielubiany przez pacjenta'">
                          <i class="fa fa-thumbs-down"></i>
                        </span>
                        <span v-show="hasPermission('creator_products_preferences')"
                              class="label label-danger"
                              :class="{hidden: !product.allergen}"
                              v-tooltip="'Produkt jest alergiczny dla pacjenta'">
                          <i class="fa fa-ban"></i>
                        </span>
                        <span v-tooltip="product.name + ' - ' + product.portion.amount + ' x ' +
                          product.portion.weight.unit">{{ product.name }}
                        </span>
                      </div>
                      <dropdown
                          :position="isFirstDay? 'left':'right'"
                          :disabled="dragAndCopyEnabled"
                          class="context__menu"
                          v-if="somePermission('creator_items_edit', 'creator_items_copy' , 'creator_items_delete')">
                        <li role="presentation" v-if="hasPermission('creator_items_edit')">
                          <a @click="addItemsModal(product)" title="Edytuj produkt">
                            <i class="fa fa-edit"></i> Edytuj
                          </a>
                        </li>
                        <li role="presentation">
                          <a class="disabled" v-if="!hasPermission('creator_products_replace')"
                             v-tooltip="trans('app.no_access_tooltip')">
                            <i class="fa fa-refresh"></i> Zamień
                          </a>
                          <a v-else @click="replaceProduct(product)" title="Zamień produkt">
                            <i class="fa fa-refresh"></i> Zamień
                          </a>
                        </li>
                        <li role="presentation" v-show="hasPermission('creator_items_copy')">
                          <v-popover>
                            <a class="hover-success" title="Kopiuj produkt">
                              <i class="fa fa-copy"></i> Kopiuj
                            </a>
                            <template slot="popover">
                              <copy-product
                                  :dayId="day.id"
                                  :planId="plan.id"
                                  :productId="product.id">
                              </copy-product>
                            </template>
                          </v-popover>
                        </li>
                        <li role="presentation" class="divider"></li>
                        <li role="presentation" v-if="hasPermission('creator_items_delete')">
                          <a @click="removeProduct(product.id)" title="Usuń produkt">
                            <i class="fa fa-times text-red"></i> Usuń
                          </a>
                        </li>
                      </dropdown>
                    </div>
                  </div>
                </draggable>

                <div class="plan__summary d-flex" v-if="plan.totalEnergy.amount > 0">
                    <span><strong>W</strong>: {{ plan.sumNutrientById(CARBO) | round(1) }}</span>
                    <span><strong>B</strong>: {{ plan.sumNutrientById(PROTEIN) | round(1) }}</span>
                    <span><strong>T</strong>: {{ plan.sumNutrientById(FATS) | round(1) }}</span>
                    <span><strong>Bł</strong>: {{ plan.sumNutrientById(FIBER) | round(1) }}</span>
                    <span><strong>ŁG</strong>: {{ plan.calculateGlicemicLoad() | round(0) }}</span>
                    <span><strong>IG</strong>: {{ plan.calculateGlicemicIndex() | round(0) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters, mapMutations, mapState} from 'vuex';
    import draggable from 'vuedraggable';
    import {dateFromTimestamp, FORMAT_DATETIME, humanizeDuration} from '../utils/dateFormatter';
    import {multiPortionFoodsManager} from '../services/MultiPortionFoodsManager';
    import {polishPlurals} from 'polish-plurals';
    import {CARBO, FATS, FIBER, PROTEIN} from "../services/NutrientDetailsProvider";
    import {copyFood, pastePlanFromClipboard, copyProduct} from "../services/CopyMenuDay";
    import {ClipboardService} from "../services/clipboard/ClipboardService";
    import {trans} from "../app";

    export default {
        props: {
            dayId: {
                type: String,
            },
            planId: {
                type: String,
            },
            write: {
                type: Boolean,
                default: false,
            },
            isAssignedToUser: {
              type: Boolean
            },
            isFirstDay: {
                type: Boolean,
            },
        },
        data() {
            return {
                dragOptions: {},
                planDate: null,
                dragActive: false,
                planCommentInputVisibility: 'hidden',
                CARBO: CARBO,
                PROTEIN: PROTEIN,
                FATS: FATS,
                FIBER: FIBER,
            };
        },
        mounted() {
            this.toggleComment = ((allCommentHidden) => {
                if (!allCommentHidden && this.plan.hasComment()) {
                    this.showPlanCommentInput();
                }
            });
            this.removeComment = (() => {
                this.plan.comment = '';
                this.hidePlanCommentInput();
            });
            this.$root.$on('remove_all_comments', this.removeComment);
            this.$root.$on('toggle_all_comments', this.toggleComment);
        },
        created() {
            this.planDate = Object.freeze(dateFromTimestamp(this.plan.date));
            if (!this.commentsHidden && this.plan.hasComment()) {
                this.showPlanCommentInput();
            }
        },
        beforeDestroy() {
            this.$root.$off('toggle_all_comments', this.toggleComment);
            this.$root.$off('remove_all_comments', this.removeComment);
        },
        components: {
            draggable,
        },
        methods: {
            ...mapMutations(['showModal', 'removePlan', 'checkPlanRepetition', 'checkFoodRepetition', 'removeDateRepetition']),
            removeFood(foodId) {
              this.$confirmDanger(trans('app.creator.foods.remove')).then(() => {
                const food = this.plan.getFood(foodId);

                if (this.hasPermission('creator_food_repetition')) {
                  this.checkFoodRepetition(food);
                }

                if (food.isMultiPortionFood()) {
                  multiPortionFoodsManager.removeMultiPortionFood(food, this.schema);
                }

                this.plan.removeFood(foodId);
                this.$root.$emit('multi_portion_food_removed_from_plan', food.isMultiPortionFood());
              }).catch((error) => {
                this.sentryCaptureException(error);
              });
            },
            remove(planId) {
              this.$confirmDanger(trans('app.creator.plans.remove')).then(() => {
                const plan = this.plan;
                this.removePlan({dayId: this.day.id, planId});
                if (this.hasPermission('creator_food_repetition')) {
                  this.checkPlanRepetition(plan);
                }
              }).catch((error) => {
                this.sentryCaptureException(error);
              });
            },
            removeProduct(productId) {
              this.$confirmDanger(trans('app.creator.products.remove')).then(() => {
                this.plan.removeProduct(productId)
              }).catch((error) => {
                this.sentryCaptureException(error);
              });
            },
            getCaloriesClasses() {
                const planRealization = this.plan.caloriesFulfillment();

                if (planRealization > 1.1) {
                    return 'text-red';
                }

                if (planRealization <= 1.1 && planRealization >= 0.9) {
                    return 'text-green';
                }

                return 'text-primary';
            },
            editFoodModal(foodId) {
                if (!this.hasPermission('creator_items_edit')) {
                    return;
                }

                this.showModal({
                    component: 'edit-food-modal',
                    data: {dayId: this.day.id, planId: this.plan.id, foodId: foodId},
                    options: {large: true},
                });
            },
            addItemsModal(product = null) {
                if (!this.hasPermission('creator_items_add')) {
                    return;
                }
                this.showModal({
                    component: 'search',
                    data: {dayId: this.day.id, planId: this.plan.id, product: product, isAssignedToUser: this.isAssignedToUser},
                    options: {large: true},
                });
            },
            repeatedFoods(food) {
                return food.repeating.map(occurrence => {
                    const dateOccurred = dateFromTimestamp(occurrence);
                    const diff = dateOccurred.diff(this.planDate);

                    return dateOccurred.format(FORMAT_DATETIME) + ' - ' + humanizeDuration(diff);
                }).join('<br>');
            },
            onFoodDragEnd(event) {
              document.querySelectorAll('.items__foods').forEach((element) => {
                element.classList.remove('items__highlight');
              });
              const target = event.to;
              const from = event.from;
              const movedFood = event.item;
              const targetPlan = this.getPlanById(target.dataset.day, target.dataset.plan);
              const fromPlan = this.getPlanById(from.dataset.day, from.dataset.plan);
              const movedFoodModel = targetPlan.getFood(movedFood.dataset.id);
              if (!this.dragAndCopyEnabled) {
                if (this.hasPermission('creator_food_repetition')) {
                  this.removeDateRepetition(movedFoodModel);
                }

                movedFoodModel.date = targetPlan.date;

                if (this.hasPermission('creator_food_repetition')) {
                  this.checkFoodRepetition(movedFoodModel);
                }
                return;
              }

              if (targetPlan.id === fromPlan.id) {
                return;
              }

              let foodCopy;
              if (movedFoodModel.isMultiPortionFood()) {
                foodCopy = multiPortionFoodsManager.copyMultiPortionFood(movedFoodModel, this.schema);
              } else {
                foodCopy = copyFood(movedFoodModel);
              }
              foodCopy.date = targetPlan.date;
              foodCopy.repeating = foodCopy.repeating.filter(d => d !== foodCopy.date);
              targetPlan.removeFood(movedFoodModel.id);
              fromPlan.addFood(movedFoodModel);
              targetPlan.addFood(foodCopy);
              this.checkFoodRepetition(foodCopy);
            },
            onFoodDragStart() {
              document.querySelectorAll('.items__foods').forEach((element) => {
                element.classList.add('items__highlight');
              });

            },
            onItemChoose() {
              this.$root.$emit('plan-drag-started');
            },
            onProductDragStart() {
              document.querySelectorAll('.items__products').forEach((element) => {
                element.classList.add('items__highlight');
              });
            },
            onProductDragEnd(event) {
              document.querySelectorAll('.items__products').forEach((element) => {
                element.classList.remove('items__highlight');
              })
              if (!this.dragAndCopyEnabled) {
                return;
              }
              const target = event.to;
              const from = event.from;
              const movedProduct = event.item;
              const targetPlan = this.getPlanById(target.dataset.day, target.dataset.plan);
              const fromPlan = this.getPlanById(from.dataset.day, from.dataset.plan);
              if (targetPlan.id === fromPlan.id) {
                return;
              }

              const movedProductModel = targetPlan.findProduct(movedProduct.dataset.id, 'id');

              let productCopy = copyProduct(movedProductModel)
              targetPlan.removeProduct(movedProductModel.id);
              fromPlan.addProduct(movedProductModel);
              targetPlan.addProduct(productCopy);
            },
            getUsedPortionsOfFood(food) {
                return multiPortionFoodsManager.getUsedPortionsOfFood(food, this.schema);
            },
            leftPortionsForFoodToolTip(food) {
                const leftPortions = multiPortionFoodsManager.getLeftPortionsOfFood(food, this.schema);

                if (leftPortions === 0) {
                    return 'Wykorzystano wszystkie porcje';
                }

                return polishPlurals('Pozostała', 'Pozostały', 'Pozostało', leftPortions) +
                    ' jeszcze ' +
                    leftPortions +
                    ' ' +
                    polishPlurals('porcja', 'porcje', 'porcji', leftPortions) +
                    ' do wykorzystania';
            },
            getMultiPortionFoodColorClass(food) {
                if (food.isMultiPortionFood() === false) {
                    return '';
                }

                return multiPortionFoodsManager.getMultiPortionFoodColor(food, this.schema);
            },
            hidePlanCommentInput() {
                this.planCommentInputVisibility = 'hidden';
            },
            showPlanCommentInput() {
                this.planCommentInputVisibility = 'visible';
            },
            getPlanCommentOnBlurListener($event) {
                this.plan.comment = this.$sanitize($event.target.innerText).replace(/\n/g, '<br>');
            },
            getPlanCommentOnOutsideClickListener() {
                if (!this.plan.hasComment()) {
                    this.hidePlanCommentInput();
                }
            },
            getPlanCommentRemoveClickListener() {
                if (this.plan.hasComment()) {
                  this.$confirmDanger(trans('app.creator.comments.remove')).then(() => {
                    this.plan.comment = '';
                    this.hidePlanCommentInput()
                  }).catch((error) => {
                    this.sentryCaptureException(error);
                  });
                }
            },
            getPlanCommentAddClickListener() {
                this.showPlanCommentInput();
                this.$nextTick(() => {
                    this.$refs.planComment.focus();
                });
            },
            isFoodWithReplacedProduct(food) {
              return this.foodsWithReplacements.filter((foodWithReplacedProduct) => {
                return food.id === foodWithReplacedProduct.id;
              }).length > 0;
            },
            isReplacedProduct(product) {
              return this.productReplacements.filter((productReplacement) => {
                return product.id === productReplacement.id;
              }).length > 0;
            },
            replaceProduct(product) {
              this.showModal({
                component: 'replace-search-product-modal',
                data: {
                  productToReplace: product,
                  plan: this.plan,
                  day: this.day
                },
              });
            },
            isFoodWithSearchedProduct(food) {
              return this.foodsWithSearchedProduct.filter((foodWithSearchedProduct) => {
                return food.id === foodWithSearchedProduct.id;
              }).length > 0;
            },
            isSearchedProduct(product) {
              return this.productsSameAsSearchedProduct.filter((searchedProduct) => {
                return product.id === searchedProduct.id;
              }).length > 0;
            },
            copyPlanToAnotherMenu(plan) {
                for (const i in plan.foods) {
                    if (plan.foods[i].isMultiPortionFood()) {
                        this.$toast(trans('app.menu.copy_multi_portion_food_warning'));
                        break;
                    }
                }
                ClipboardService.addPlan(plan, {menuId: this.MENU_ID});
                this.$toast(trans('app.menu.plan_copied'));
            },
            pastePlanFromAnotherMenu(currentPlan) {
                pastePlanFromClipboard(currentPlan);
                this.checkPlanRepetition(currentPlan);
            },
        },
        computed: {
            ...mapState({
                schema: state => state.menu.schema,
            }),
            ...mapGetters([
                'getDayById',
                'getPlanById',
                'hasPermission',
                'somePermission',
                'commentsHidden',
                'foodsWithReplacements',
                'productReplacements',
                'foodsWithSearchedProduct',
                'productsSameAsSearchedProduct',
                'dragAndCopyEnabled',
                'MENU_ID',
            ]),
            day() {
                return this.getDayById(this.dayId);
            },
            plan() {
                return this.day.plans.find((plan) => plan.id === this.planId);
            },
            hasPlanInClipboard() {
                if (ClipboardService.hasPlan() === false) {
                    return false;
                }
                const plan = ClipboardService.getPlan();
                return plan.metadata.menuId !== this.MENU_ID;
            }
        },
    };
</script>

<style lang="scss" type="text/scss">
    .d-flex {
        display: flex;
    }

    .flex-row {
        flex-flow: row;
    }

    .plan__card {
        margin-bottom: 5px;
        background-color: #fff;
        border-radius: 3px;
        box-shadow: 0 1px 0 #ccc;
        display: block;
        min-height: 20px;
        position: relative;
        text-decoration: none;

        .plan__card_details {
            padding: 5px 8px;
        }
        .plan__card_details>.plan__summary:last-child {
          border-bottom: none;
          flex-wrap: wrap;
        }
        .plan__card_details>.plan__summary:last-child > span {
          flex-grow: 1;
          width: 33%;
        }
    }

    .justify-content-between {
        justify-content: space-between;
    }

    .plan__header {
        margin: 2px 0;

        .plan__name {
            font-size: 13px;
            cursor: pointer;
            font-weight: bold;
            margin: 0;
            line-height: 18px;
            overflow-wrap: break-word;
            width: 90%;
        }

        .plan__actions {
            li {
                display: inline-block;
            }
        }
    }

    .plan.selected {
      .plan__card {
        background-color: rgb(0,0,0, 0.05);
        .plan__card_details {
          .plan__header {
            .plan__name {
              color: #00a65a;
            }
          }
        }
      }
    }

    .plan__actions {
        a {
            color: #b8b0b7;
        }
    }

    .item--offset {
        padding-left: 3px;
        padding-right: 3px;
    }

    .plan__items {
        font-size: 12px;
        min-height: 20px;

        .plan__item {
            position: relative;
            padding: 2px;
            padding-right: 15px;
            cursor: pointer;
            border-radius: 3px;

            &:hover {

            }

        }
    }

    .items__highlight {
      border: 1px solid #00a65a;
      border-radius: 5px;
      background-color: rgb(0,150,32,0.3);
    }

    .plan__summary {
        border: 1px solid #ddd;
        font-size: 11px;
        border-left: 0;
        border-right: 0;
        margin: 5px 0;
    }

    .plan__item:hover {
        background: #eee;

        .context__menu {
            visibility: visible;
        }
    }

    .context__menu {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 4px;
        z-index: 99;
        text-align: center;
        line-height: 20px;
        border-radius: 3px;
        /*background: #eee;*/
        /*visibility: hidden;*/
        /*opacity: 0.8;*/
        &:hover {
            opacity: 1;
            background: #b8b0b7;
        }

        &:hover > li > a {
            color: #fff;
        }
    }

    .sortable-ghost {
        background: #ddd;
        border-radius: 3px;
        box-shadow: none;
    }

    .sortable-choosen {
        background-color: whitesmoke;
        border-radius: 3px;
        box-shadow: 0 1px 0 #ccc;
    }

    .hover-remove:hover {
        color: #dd4b39;
    }

    .hover-success:hover {
        color: #00a65a;
    }

    .trigger {
        width: 100%;
    }

    .plan__comment {
        position: relative;
    }

    .plan__comment_input:hover:not(:focus) {
        cursor: pointer;
    }

    .plan__comment_input:hover {
        cursor: auto;
    }

    .plan__comment_input:focus {
        background: #fff;
    }

    .plan__comment_input:empty:before {
        content: attr(placeholder);
        pointer-events: none;
        display: block;
    }

    .green-border {
      border: thin solid #4caf50;
    }

    .green-text {
      color: #4caf50;
    }
</style>
